<template>
  <v-card class="resident-card">
    <v-card-text>
      <v-row class="mt-4">
        <v-col cols="5">
          <span class="h5 font-weight-bold black--text">{{ $t('AddFamilyMemberDialog.FinancialPOA(FPOA)') }}:</span>
        </v-col>
        <v-col cols="7">
          <v-tooltip v-if="canChangePoaStatus" bottom :nudge-bottom="-15" :nudge-left="0">
              <template v-slot:activator="{ on, attrs }">
                <div v-on="on" v-bind="attrs">
                  <v-checkbox v-model="isFinancialEPOAModel" :disabled="disabled || !canChangePoaStatus" />
                </div>
              </template>
              <span >{{$t('ResidentCard.ChangesMadeWillNotReflectInMCare')}}</span>
          </v-tooltip>
          <v-checkbox v-else v-model="isFinancialEPOAModel" :disabled="disabled || !canChangePoaStatus" />
        </v-col>
      </v-row>
      <v-row class="mt-4">
        <v-col cols="5">
          <span class="h5 font-weight-bold black--text">{{ $t('AddFamilyMemberDialog.WelfarePOA(WPOA)') }}:</span>
        </v-col>
        <v-col cols="7">
           <v-tooltip v-if="canChangePoaStatus" bottom :nudge-bottom="-20" :nudge-left="0">
              <template v-slot:activator="{ on, attrs }">
                <div v-on="on" v-bind="attrs">
                  <v-checkbox v-model="isWelfarePOAModel" :disabled="disabled || !canChangePoaStatus" />
                </div>
              </template>
              <span>{{$t('ResidentCard.ChangesMadeWillNotReflectInMCare')}}</span>
          </v-tooltip>
          <v-checkbox v-else v-model="isWelfarePOAModel" :disabled="disabled || !canChangePoaStatus" />
        </v-col>
      </v-row>
      <v-row class="mt-4">
        <v-col cols="5">
          <span class="h5 font-weight-bold black--text">{{ $t('AddFamilyMemberDialog.EnduringPOA(EPOA)') }}:</span>
        </v-col>
        <v-col cols="7">
          <v-tooltip v-if="canChangePoaStatus" bottom :nudge-bottom="-15" :nudge-left="0">
              <template v-slot:activator="{ on, attrs }">
                <div v-on="on" v-bind="attrs">
                  <v-checkbox :key="enduringPOARenderKey" :input-value="isEnduringPOA" :disabled="disabled || !canChangePoaStatus" @change="confirmEnduringPOAChange" />
                </div>
              </template>
              <span>{{$t('ResidentCard.ChangesMadeWillNotReflectInMCare')}}</span>
          </v-tooltip>
          <v-checkbox v-else :key="enduringPOARenderKey" :input-value="isEnduringPOA" :disabled="disabled || !canChangePoaStatus" @change="confirmEnduringPOAChange" />
        </v-col>
      </v-row>
      
      <v-row>
        <v-col cols="5">
          <span class="h5 font-weight-bold black--text">{{ $t('GeneralTranslations.Resident') }}:</span>
        </v-col>
        <v-col cols="5">
          <v-avatar size="42" class="ml-4">
            <img :src="resident.imageUrl" />
          </v-avatar>
          <span class="ml-3 h6">{{ resident.name }}</span>
        </v-col>
        <v-col cols="2" class="d-flex justify-end">
          <router-link :to="`/residents/${resident.id}`">
            <v-btn uppercase text color="primary" class="font-weight-bold">
              {{ $t('GeneralTranslations.View') }}
            </v-btn>
          </router-link>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="5">
          <span class="h5 font-weight-bold black--text">{{ $t('ResidentCard.RelationshipToResident') }}:</span>
        </v-col>
        <v-col cols="7">
          <span class="ml-4 h6">{{ residentRelationshipType }}</span>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="5">
          <span class="h5 font-weight-bold black--text">{{ $t('GeneralTranslations.Room') }}:</span>
        </v-col>
        <v-col cols="7">
          <span class="ml-4 h6">{{ resident.wingName }}, {{ resident.roomName }}</span>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="5">
          <span class="h5 font-weight-bold black--text">{{ $t('GeneralTranslations.DOB') }}:</span>
        </v-col>
        <v-col cols="7">
          <span class="ml-4 h6">{{ dateOfBirthDisplay }}</span>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="5">
          <span class="h5 font-weight-bold black--text">{{ $t('GeneralTranslations.Status') }}:</span>
        </v-col>
        <v-col cols="7">
          <span v-if="resident.status === 'Departed'" class="ml-4 h6 departed">{{  $t('ResidentStatus.Departed') }}</span>
          <span v-else class="ml-4 h6">{{ $t(`ResidentStatus.${resident.status.replace(' ','')}`)}} </span>
        </v-col>
      </v-row> 
      <v-row>
        <v-col cols="5">
          <span class="h5 font-weight-bold black--text">{{ $t('FamilyView.ResidentConsent') }}:</span>
        </v-col>
        <v-col cols="7">
          <v-checkbox :input-value="getFamilyResidentEnabled('residentConsent')" @change="val => setFamilyResidentEnabled('residentConsent', val)"/>
        </v-col>
      </v-row> 
      <v-row>
        <v-col cols="12">
          <v-expansion-panels accordion>
              <v-expansion-panel>
                <v-expansion-panel-header>App Access</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-container v-if="facility?.isEnableCareTab ?? false">
                    <v-checkbox
                      :input-value="getAccessEnabled('care')"
                      @change="val => setAccessEnabled('care', val)"
                      label="Care Tab"
                    />

                    <v-container v-if="getAccessEnabled('care')">
                      <v-checkbox
                        v-if="facility.isCareSummary ?? false"
                        :input-value="getAccessEnabled('careSummary')"
                        @change="val => setAccessEnabled('careSummary', val)"
                        label="Care Summary"
                      />
                      <v-checkbox
                        v-if="facility.isDailyCare ?? false"
                        :input-value="getAccessEnabled('dailyCare')"
                        @change="val => setAccessEnabled('dailyCare', val)"
                        label="Daily Care"
                      />
                      <v-checkbox
                        v-if="facility.isDailyPlan ?? false"
                        :input-value="getAccessEnabled('dailyPlan')"
                        @change="val => setAccessEnabled('dailyPlan', val)"
                        label="Daily Plan"
                      />
                      <v-checkbox
                        v-if="(facility.isCarePlan && facility.isEnabledCarePlan) ?? false"
                        :input-value="getAccessEnabled('carePlan')"
                        @change="val => setAccessEnabled('carePlan', val)"
                        hide-details
                        label="Care Plan"
                      />
                      <p v-if="getAccessEnabled('carePlan') && !isEnduringPOAModel" class="my-2">{{ $t("FamilyView.WarningEPOA") }}</p>
                    </v-container>

                    <v-container>
                      <v-checkbox
                        v-if="facility.additionalServicesEnabled"
                        :input-value="getAccessEnabled('concierge')"
                        @change="val => setAccessEnabled('concierge', val)"
                        label="Concierge"
                      />
                      <v-checkbox
                        :input-value="getAccessEnabled('instantCalls')"
                        @change="val => setAccessEnabled('instantCalls', val)"
                        label="Instant Calls"
                      />
                      <v-checkbox
                        v-if="facility.scheduledCallsEnabled"
                        :input-value="getAccessEnabled('scheduledCalls')"
                        @change="val => setAccessEnabled('scheduledCalls', val)"
                        label="Scheduled Calls"
                      />
                      <v-checkbox
                        v-if="facility.familyChatEnabled"
                        :input-value="getAccessEnabled('chat')"
                        @change="val => setAccessEnabled('chat', val)"
                        label="Chat"
                      />
                    </v-container>
                  </v-container>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
        </v-col>
      </v-row>
    </v-card-text>
    <ConfirmDlg ref="enduringPOAConfirmDlg" />
  </v-card>
</template>

<script>
import moment from 'moment';
import { fillMissingFamilyResidentAccess, fillMissingFamilyResidentSettings } from '@/utils/familyResidentAccess';
import { relationshipTypes } from '@/consts/relationship-types';
import { hasAccess } from '@/utils/accessControl/accessControl';
import { AccessMatrixKeys } from '@/utils/accessControl/accessMatrixKeysEnum';

export default {
  components: {
    ConfirmDlg: () => import('@/components/popup/confirm-dialog/ConfirmDlg.vue'),
  },
  props: {
    resident: {
      type: Object,
      required: true,
    },
    isEnduringPOA: {
      type: Boolean,
      default: false,
    },
    isFinancialEPOA: {
      type: Boolean,
      default: false,
    },
    isWelfarePOA: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    relationshipToResident: {
      type: String,
      default: ""
    },
    familyResidentsAccesses:{
      type: Array
    },
    familyResidentSettings:{
      type: Array
    },
    isNextOfKin: {
      type: Boolean,
      default: false,
    },
  },
  data(){
    return{ 
      dateOfBirthDisplay: '',
      localAccesses: [],
      localFamilyResidentSettings: [],
      enduringPOARenderKey: 0
    } 
  },
  computed: {
    facility() {
      return this.$store.state.facilityModule.selectedFacility;
    },
    user(){
      return this.$store.state.authModule.user
    },
    isFacilityHasCareAPI() {
      return this.$store.state.facilityModule.isFacilityHasCareAPI;
    },
    canChangePoaStatus(){
      return this.facility?.isEnableCareTab && hasAccess(this.user.accessLevel, AccessMatrixKeys.FamilyMembers_Details_ChangePoaStatus)
    },
    residentRelationshipType(){
      return this.relationshipToResident?.length && relationshipTypes.includes(this.relationshipToResident) ?  this.$t(`RelationshipTypes.${this.relationshipToResident}`) : (this.relationshipToResident || '')
    },
    isFinancialEPOAModel: {
      get() {
        return this.isFinancialEPOA;
      },
      set(value) {
        this.$emit('change', {
          isFinancialEPOA: {
            id: this.resident.id,
            status: value,
          }
        });
      },
    },
    isEnduringPOAModel: {
      get() {
        return this.isEnduringPOA;
      },
      set(value) {
        this.$emit('change', {
          isEnduringPOA: {
            id: this.resident.id,
            status: value,
          }
        });

        if (value) {
          this.enableAllAppAccess();
        }
      },
    },
    isWelfarePOAModel: {
      get() {
        return this.isWelfarePOA;
      },
      set(value) {
        this.$emit('change', {
          isWelfarePOA: {
            id: this.resident.id,
            status: value,
          }
        });
      },
    },
    isNextOfKinComputed: {
      get() {
        return this.isNextOfKin;
      },
      set(value) {
        this.$emit('update:isNextOfKin', {
          id: this.resident.id,
          isNextOfKin: value,
        });
      },
    },
    accessMap: {
      get(){
        const map = {};
        for (const access of this.localAccesses) {
          if (access.residentId === this.resident.id) {
            map[access.name] = access;
          }
        }
        return map;
      }
    },
    familyResidentSettingsMap: {
      get(){
        const map = {};
        for (const setting of this.localFamilyResidentSettings) {
          if (setting.residentId === this.resident.id) {
            map[setting.name] = setting;
          }
        }
        return map;
      }
    }
  },
  created(){
     this.dateOfBirthDisplay = moment(new Date(this.resident.dateOfBirth.seconds * 1000)).format(this.facility.dateFormat)
     this.familyResidentsAccesses = fillMissingFamilyResidentAccess(this.familyResidentsAccesses, this.resident.id);
     this.familyResidentSettings = fillMissingFamilyResidentSettings(this.familyResidentSettings, this.resident.id);
     console.log('familyResidentsAccesses: ', this.familyResidentsAccesses)
     console.log('familyResidentSettings: ', this.familyResidentSettings)
  },
  methods:{
    getAccessEnabled(name) {
      return this.accessMap[name]?.isEnabled ?? false;
    },
    setAccessEnabled(name, value) {
      const target = this.localAccesses.find(a => a.name === name && a.residentId === this.resident.id);
      if (target){
        target.isEnabledByStaff = value;
        target.isEnabled = value;
      }

      this.$emit('onFamilyResidentsAccessChanged', target);
    },
    getFamilyResidentEnabled(name) {
      return this.familyResidentSettingsMap[name]?.value ?? false;
    },
    setFamilyResidentEnabled(name, value) {
      const target = this.localFamilyResidentSettings.find(a => a.name === name && a.residentId === this.resident.id);
      if (target) target.value = value;

      this.$emit('onFamilyResidentSettingsChanged', target);
    },
    async confirmEnduringPOAChange(value) {
      const response = await this.$refs.enduringPOAConfirmDlg.open(
        null,
        this.$t('ResidentCard.ConfirmEnduringPOAChange'),
        false,
        '',
        { noButtonText: this.$t('GeneralTranslations.Cancel') }
      );

      if (!response || !response.agree) {
        // force a re-render so the checkbox falls back to the unchanged prop value
        this.enduringPOARenderKey += 1;
        return;
      }

      this.isEnduringPOAModel = value;
    },
    enableAllAppAccess() {
      const names = ['care', 'careSummary', 'dailyCare', 'dailyPlan', 'carePlan', 'concierge', 'instantCalls', 'scheduledCalls', 'chat'];

      for (const name of names) {
        this.setAccessEnabled(name, true);
      }
    }
  },
  watch: {
  resident: {
    handler() {
      this.localAccesses = fillMissingFamilyResidentAccess(this.familyResidentsAccesses, this.resident.id);
      this.localFamilyResidentSettings = fillMissingFamilyResidentSettings(this.familyResidentSettings, this.resident.id);
    },
    immediate: true,
    deep: true
  }
}
};
</script>

<style scoped>
.departed {
  color: red;
}

.resident-card{
  box-shadow:none!important;
  border: 1px solid #EDEFFB !important;
}
</style>
